import { useState, useCallback, useEffect } from 'react';

interface WalletState {
  isConnected: boolean;
  address: string | null;
  balance: string;
  network: string | null;
  isConnecting: boolean;
}

export const useWallet = () => {
  const [walletState, setWalletState] = useState<WalletState>({
    isConnected: false,
    address: null,
    balance: '0',
    network: null,
    isConnecting: false
  });

  const connectWallet = useCallback(async () => {
    if (typeof window !== 'undefined' && window.ethereum) {
      setWalletState(prev => ({ ...prev, isConnecting: true }));
      
      try {
        const accounts = await window.ethereum.request({
          method: 'eth_requestAccounts',
        });
        
        const networkId = await window.ethereum.request({
          method: 'net_version',
        });
        
        // Get balance
        const balance = await window.ethereum.request({
          method: 'eth_getBalance',
          params: [accounts[0], 'latest'],
        });
        
        const balanceInEth = (parseInt(balance, 16) / Math.pow(10, 18)).toFixed(4);
        
        setWalletState({
          isConnected: true,
          address: accounts[0],
          balance: balanceInEth,
          network: networkId,
          isConnecting: false
        });
      } catch (error) {
        console.error('Failed to connect wallet:', error);
        setWalletState(prev => ({ ...prev, isConnecting: false }));
      }
    } else {
      alert('Please install MetaMask or another Web3 wallet!');
    }
  }, []);

  const disconnectWallet = useCallback(() => {
    setWalletState({
      isConnected: false,
      address: null,
      balance: '0',
      network: null,
      isConnecting: false
    });
  }, []);

  // Check if wallet is already connected
  useEffect(() => {
    const checkConnection = async () => {
      if (typeof window !== 'undefined' && window.ethereum) {
        try {
          const accounts = await window.ethereum.request({
            method: 'eth_accounts',
          });
          
          if (accounts.length > 0) {
            const networkId = await window.ethereum.request({
              method: 'net_version',
            });
            
            const balance = await window.ethereum.request({
              method: 'eth_getBalance',
              params: [accounts[0], 'latest'],
            });
            
            const balanceInEth = (parseInt(balance, 16) / Math.pow(10, 18)).toFixed(4);
            
            setWalletState({
              isConnected: true,
              address: accounts[0],
              balance: balanceInEth,
              network: networkId,
              isConnecting: false
            });
          }
        } catch (error) {
          console.error('Error checking wallet connection:', error);
        }
      }
    };
    
    checkConnection();
  }, []);

  return {
    ...walletState,
    connectWallet,
    disconnectWallet
  };
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    ethereum?: any;
  }
}